import React from "react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, Info, XCircle, ShoppingCart } from "lucide-react";
import { gbp } from "@/components/analytics/AnalyticsParts";

const num = (v) => (v == null ? "–" : Math.round(Number(v)).toLocaleString("en-GB"));
const pct = (v) => (v == null ? "–" : `${(Number(v) * 100).toFixed(1)}%`);
const secs = (v) => (v == null ? "–" : `${Math.floor(v / 60)}m ${Math.round(v % 60)}s`);
export const KPI_DEFS = {
  activeUsers: ["Active users", num], newUsers: ["New users", num], totalUsers: ["Total users", num], sessions: ["Sessions", num],
  engagedSessions: ["Engaged sessions", num], engagementRate: ["Engagement rate", pct], avgEngagementTime: ["Avg engagement time", secs],
  averageSessionDuration: ["Avg session duration", secs], screenPageViews: ["Page views", num], keyEvents: ["GA4 key events", num],
  eventCount: ["Events", num], qualifiedEnquiries: ["Qualified enquiries", num], ecommercePurchases: ["GA4 purchases", num],
  purchaseRevenue: ["GA4 revenue", gbp], addToCarts: ["Add to carts", num], checkouts: ["Checkouts", num],
  wooOrders: ["WooCommerce orders", num], wooRevenue: ["WooCommerce revenue", gbp],
};
export const SECTION_LABELS = { quality: "Data quality", kpis: "KPI cards", enquiries: "Customer actions", trend: "Traffic trend", channels: "Marketing channels",
  woo: "WooCommerce vs GA4", sources: "Source / medium", campaigns: "Campaigns", utm: "UTM parameters", landing: "Landing pages",
  enquiry_pages: "Pages with enquiries", pages: "Top pages", events: "All GA4 events", history: "Monthly history" };
const COLS = { sessions: ["Sessions", num], activeUsers: ["Users", num], engagementRate: ["Eng. rate", pct], keyEvents: ["Key events", num],
  purchaseRevenue: ["GA4 rev.", gbp], screenPageViews: ["Views", num], eventCount: ["Events", num], totalUsers: ["Users", num] };

export const Box = ({ title, children, testid, right }) => (
  <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3"><h2 className="text-base font-bold text-slate-900">{title}</h2>{right}</div>{children}
  </section>
);

export const Delta = ({ cur, prev, invert }) => {
  if (prev == null || cur == null || !prev) return null;
  const d = (cur - prev) / prev;
  const up = d >= 0;
  const good = invert ? !up : up;
  const I = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center text-[11px] font-semibold ${good ? "text-emerald-600" : "text-rose-600"}`}><I className="w-3 h-3" />{Math.abs(d * 100).toFixed(1)}%</span>;
};

export function Kpis({ r, kpis }) {
  const t = { ...r.totals, wooOrders: r.woo?.orders, wooRevenue: r.woo?.revenue };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ga-kpis">
      {kpis.filter((k) => KPI_DEFS[k]).map((k) => {
        const [label, f] = KPI_DEFS[k];
        const missing = t[k] == null;
        return (
          <div key={k} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid={`ga-kpi-${k}`}>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
            <div className="text-xl font-bold text-slate-900 mt-1">{missing ? <span className="text-sm font-medium text-slate-400">{k === "qualifiedEnquiries" ? "Confirm mapping" : "Not collected"}</span> : f(t[k])}</div>
            <Delta cur={t[k]} prev={r.previous?.[k]} />
          </div>
        );
      })}
    </div>
  );
}

export function Enquiries({ r }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ga-enquiries">
      {Object.entries(r.enquiries).map(([k, c]) => (
        <div key={k} className={`rounded-2xl border p-4 ${c.confirmed ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`} data-testid={`ga-enq-${k}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{c.configured ? num(c.current) : "–"}</div>
          <div className="text-[11px] mt-0.5 flex items-center gap-1.5">
            <span className={c.confirmed ? "text-emerald-700" : c.configured ? (c.current ? "text-amber-700" : "text-rose-600") : "text-slate-400"}>
              {c.confirmed ? "Confirmed" : !c.configured ? "Not mapped" : c.current ? "Unconfirmed" : "Not tracked"}</span>
            <Delta cur={c.current} prev={c.previous} />
          </div>
        </div>
      ))}
    </div>
  );
}

const fmtX = (grain) => (v) => grain === "yearMonth" ? `${v.slice(4)}/${v.slice(2, 4)}` : grain === "isoYearIsoWeek" ? `W${v.slice(4)}` : v.slice(5);
export function Trend({ r, grain }) {
  const rows = r.trend[grain] || [];
  const key = grain === "date" ? "date" : grain;
  if (!rows.length) return <p className="text-sm text-slate-400">This grain isn't selected in Data selection.</p>;
  return (
    <div className="h-72" data-testid="ga-trend-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" />
          <XAxis dataKey={key} tick={{ fontSize: 11 }} tickFormatter={fmtX(grain)} minTickGap={16} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
          {"sessions" in (rows[0] || {}) && <Bar dataKey="sessions" name="Sessions" fill="#e0e7ff" radius={[3, 3, 0, 0]} />}
          {"activeUsers" in (rows[0] || {}) && <Line dataKey="activeUsers" name="Active users" stroke="#4f46e5" strokeWidth={2} dot={false} />}
          {"engagedSessions" in (rows[0] || {}) && <Line dataKey="engagedSessions" name="Engaged sessions" stroke="#059669" strokeWidth={2} dot={false} />}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function DataTable({ rows, dims, labels, testid }) {
  if (!rows) return <p className="text-sm text-slate-400">Turn on the needed dimensions in Configure Analytics → Data selection.</p>;
  const mets = Object.keys(rows[0] || {}).filter((k) => COLS[k]);
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left">
          {dims.map((d, i) => <th key={d} className="py-2 pr-3 font-semibold">{labels[i] || d}</th>)}
          {mets.map((m) => <th key={m} className="py-2 px-2 font-semibold text-right">{COLS[m][0]}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              {dims.map((d) => <td key={d} className="py-2 pr-3 text-slate-700 max-w-[240px] truncate" title={r[d]}>{r[d] || "(not set)"}</td>)}
              {mets.map((m) => <td key={m} className="py-2 px-2 text-right">{COLS[m][1](r[m])}</td>)}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={dims.length + mets.length || 1} className="py-3 text-slate-400">No data for this period.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function Channels({ r }) {
  const has = r.previous != null;
  return (
    <div className="overflow-x-auto" data-testid="ga-channels">
      <table className="w-full text-sm">
        <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 text-left"><th className="py-2 pr-3">Channel</th><th className="py-2 px-2 text-right">Sessions</th>
          <th className="py-2 px-2 text-right">Users</th><th className="py-2 px-2 text-right">Engaged</th><th className="py-2 px-2 text-right">Key events</th>{has && <th className="py-2 pl-2 text-right">vs prev.</th>}</tr></thead>
        <tbody>{r.channels.map((c) => (
          <tr key={c.channel} className="border-t border-slate-100" data-testid={`ga-channel-${c.channel}`}>
            <td className="py-2 pr-3 font-medium text-slate-700">{c.channel}</td><td className="py-2 px-2 text-right">{num(c.current.sessions)}</td>
            <td className="py-2 px-2 text-right">{num(c.current.activeUsers)}</td><td className="py-2 px-2 text-right">{num(c.current.engagedSessions)}</td>
            <td className="py-2 px-2 text-right">{num(c.current.keyEvents)}</td>{has && <td className="py-2 pl-2 text-right"><Delta cur={c.current.sessions} prev={c.previous.sessions} /></td>}
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export const Woo = ({ r }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ga-woo">
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4"><div className="flex items-center gap-2 text-xs font-semibold text-indigo-800"><ShoppingCart className="w-4 h-4" />Actual: WooCommerce orders</div>
      <div className="text-2xl font-bold text-slate-900 mt-2" data-testid="ga-woo-revenue">{gbp(r.woo.revenue)}</div><div className="text-xs text-slate-500">{num(r.woo.orders)} orders · {r.woo.source}</div></div>
    <div className="rounded-xl border border-slate-200 p-4"><div className="text-xs font-semibold text-slate-600">GA4 attributed (website tracking)</div>
      <div className="text-2xl font-bold text-slate-900 mt-2" data-testid="ga-ga4-revenue">{gbp(r.totals.purchaseRevenue)}</div><div className="text-xs text-slate-500">{num(r.totals.ecommercePurchases)} GA4 purchases · not the same as real orders</div></div>
  </div>
);

export function QualityBar({ items }) {
  const [open, setOpen] = React.useState(false);
  const n = (l) => (items || []).filter((q) => q.level === l).length;
  const [e, w, i] = [n("error"), n("warning"), n("info")];
  const tone = e ? "border-rose-200 bg-rose-50" : w ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50";
  return (
    <section className={`rounded-2xl border ${tone}`} data-testid="ga-sec-quality">
      <button onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-3 px-4 py-2.5 text-left" data-testid="ga-quality-toggle">
        <AlertTriangle className={`w-4 h-4 ${e ? "text-rose-600" : w ? "text-amber-600" : "text-emerald-600"}`} />
        <span className="text-sm font-semibold text-slate-800">Data quality</span>
        <span className="text-xs text-slate-600" data-testid="ga-quality-summary">{e + w + i ? `${e} error${e === 1 ? "" : "s"} · ${w} warning${w === 1 ? "" : "s"} · ${i} note${i === 1 ? "" : "s"}` : "No tracking problems found"}</span>
        <span className="ml-auto text-xs font-medium text-indigo-700">{open ? "Hide" : "View details"}</span>
      </button>
      {open && <div className="px-4 pb-4"><Quality items={items} /></div>}
    </section>
  );
}

const LV = { error: [XCircle, "text-rose-700 bg-rose-50"], warning: [AlertTriangle, "text-amber-800 bg-amber-50"], info: [Info, "text-slate-600 bg-slate-50"] };
export const Quality = ({ items }) => !items?.length ? <p className="text-sm text-emerald-700">No tracking problems found.</p> : (
  <ul className="space-y-2" data-testid="ga-quality-list">
    {items.map((q, i) => { const [I, c] = LV[q.level] || LV.info; return (
      <li key={i} className={`flex gap-2 rounded-xl px-3 py-2 text-sm ${c}`} data-testid={`ga-quality-${q.code}`}><I className="w-4 h-4 mt-0.5 shrink-0" /><div><b>{q.title}</b><div className="text-xs opacity-90">{q.detail}</div></div></li>); })}
  </ul>
);

export function History({ months }) {
  if (!months?.length) return <p className="text-sm text-slate-400" data-testid="ga-history-empty">No history yet. Run a backfill in Configure Analytics → Data settings.</p>;
  const rows = months.map((m) => ({ month: m.month, sessions: m.metrics.sessions, activeUsers: m.metrics.activeUsers, enquiries: Object.values(m.enquiries || {}).reduce((a, b) => a + b, 0) }));
  return (
    <div className="h-72" data-testid="ga-history-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={rows} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid stroke="#f1f5f9" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis yAxisId="a" tick={{ fontSize: 11 }} /><YAxis yAxisId="b" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="a" dataKey="sessions" name="Sessions" fill="#e0e7ff" /><Line yAxisId="a" dataKey="activeUsers" name="Active users" stroke="#4f46e5" dot={false} strokeWidth={2} />
          <Line yAxisId="b" dataKey="enquiries" name="Mapped events" stroke="#059669" dot={false} strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
